/** Central rendering constants (geometry, colors, typography) — Miragon corporate identity. */

import { MIRAGON } from "../theme/index.js";
import type { StickyKind } from "../model/di-types.js";
import { plainNoteText } from "./note-markdown.js";

export interface StickyStyle {
  /** Default label for a freshly created sticky. */
  readonly label: string;
  readonly width: number;
  readonly height: number;
  readonly fill: string;
  readonly stroke: string;
}

/**
 * The canonical sticky-note look per kind (Brandolini's color grammar): default label, fixed
 * size and fill/border. Single source for the renderer, element factory, palette previews and
 * the color-picker swatches. Aggregate, policy and external system share the large block format —
 * they occupy the same slot in the grammar (receive commands / react to events); actors are the
 * small corner stickies. Each border is the fill's hue, dark enough for 3:1 on the canvas ground:
 * the light fills barely differ from the ground, so the border carries the sticky's outline.
 */
// The sticky colours are notation, not palette colours (guide §10.6), and theme.sync checks them.
// brand-lint-disable
export const STICKY_STYLES: Record<StickyKind, StickyStyle> = {
  event: { label: "Domain Event", width: 130, height: 90, fill: "#FFB84D", stroke: "#CA7E16" },
  command: { label: "Command", width: 130, height: 90, fill: "#7EC8F0", stroke: "#2F98D1" },
  actor: { label: "Actor", width: 100, height: 65, fill: "#FFF9B1", stroke: "#9E911E" },
  aggregate: { label: "Aggregate", width: 180, height: 110, fill: "#FFE066", stroke: "#AB8C1C" },
  policy: { label: "Policy", width: 180, height: 110, fill: "#C9A0DC", stroke: "#A97CC4" },
  readmodel: { label: "Read Model", width: 130, height: 90, fill: "#A8D08D", stroke: "#6A9D4A" },
  external: {
    label: "External System",
    width: 180,
    height: 110,
    fill: "#F4A6C0",
    stroke: "#D96C95",
  },
  hotspot: { label: "Hotspot", width: 130, height: 90, fill: "#E85D75", stroke: "#C43B55" },
} as const;

/** Free-text note: grows with its text (see `noteMetrics`) unless resized by hand, neutral grey. */
export const NOTE_STYLE = { label: "Note", fill: "#ECECEC", stroke: MIRAGON.kontur } as const;
// brand-lint-enable

/**
 * Provisional (blank append) sticky: plain paper with a dashed border in `kontur`, no kind yet.
 * The border is all that sets it off the canvas ground, so it keeps 3:1 there (guide §3.6).
 */
export const PROVISIONAL_STICKY = {
  fill: MIRAGON.weiss,
  stroke: MIRAGON.kontur,
  dash: "6 4",
} as const;

/** Soft drop shadow that makes stickies read as paper, in the ink of the design system's shadows. */
export const STICKY_SHADOW = { color: MIRAGON.schwarz, opacity: 0.2, dy: 5, blur: 5 } as const;

/** Smallest hand-resized note box — keeps a shrunken note grabbable and its handles apart. */
export const NOTE_MIN_RESIZE = { width: 60, height: 40 } as const;

/** Corner radius shared by all stickies (and the palette previews) — 0: paper has sharp corners. */
export const STICKY_RADIUS = 0;
/** Inner text padding of a sticky (px, each side). */
export const STICKY_PADDING = 8;

export const STICKY_LINE_HEIGHT = 17;
/** Estimated character width at the 13px label size — good enough for wrapping/box sizing. */
export const STICKY_CHAR_WIDTH = 7.5;
/** Hanging indent (px) of note bullet lines — '•' rows and their wrapped continuations. */
export const NOTE_BULLET_INDENT = 12;
/**
 * Default note box — the aggregate block format, so a fresh note reads as a full sticky instead of
 * a text-sized speck. Notes only grow beyond it; shrinking below it stays a hand resize
 * (`NOTE_MIN_RESIZE`).
 */
const NOTE_DEFAULT_SIZE = {
  width: STICKY_STYLES.aggregate.width,
  height: STICKY_STYLES.aggregate.height,
} as const;

/**
 * Box dimensions of a note from its (possibly multi-line) text. The note shape grows as large as
 * its text -> the move/click hitbox grows with it (never below `NOTE_DEFAULT_SIZE`). Vertical
 * padding matches the renderer's clip padding (`STICKY_PADDING`) — a smaller padding here would
 * make the renderer's maxLines clip drop the last text line. Measured on `plainNoteText`: the
 * markdown markers are invisible on canvas, so they must not widen the box (bullets count their
 * visible '• ' prefix).
 */
export function noteMetrics(label: string): { lines: string[]; width: number; height: number } {
  const plain = plainNoteText(label);
  const lines = (plain && plain.length ? plain : "note").split("\n");
  const maxLen = Math.max(1, ...lines.map((l) => l.length));
  return {
    lines,
    width: Math.max(
      NOTE_DEFAULT_SIZE.width,
      Math.round(maxLen * STICKY_CHAR_WIDTH) + STICKY_PADDING * 2,
    ),
    height: Math.max(
      NOTE_DEFAULT_SIZE.height,
      lines.length * STICKY_LINE_HEIGHT + STICKY_PADDING * 2,
    ),
  };
}

/**
 * The auto-vs-manual rule: a note box is MANUAL iff it differs from `noteMetrics(label)`.
 * Single comparison shared by the exporter (emit `size` only for manual boxes), relabel
 * (preserve manual boxes) and copy-paste (recompute only auto clones) — divergence between
 * these callers would make boards change on a plain reload.
 */
export function isManualNoteBox(label: string, box: { width: number; height: number }): boolean {
  const metrics = noteMetrics(label);
  return box.width !== metrics.width || box.height !== metrics.height;
}

/**
 * Canvas colours — Miragon palette. Keys are the public contract; brand values come from the
 * single source of truth in `theme/palette.ts` (`MIRAGON`). Referenced (not spread) so the
 * literal-string types survive into the published `.d.ts`. The sticky fills live in
 * `STICKY_STYLES` — this map only carries the non-sticky canvas colours.
 */
export const COLORS = {
  paper: MIRAGON.grau,
  ink: MIRAGON.schwarz,
  inkSoft: MIRAGON.textLeise,
  surface: MIRAGON.weiss,
  stroke: MIRAGON.schwarz,
  /** The single connection style: an arrowed line in `kontur`, as edges are (guide §10.1). */
  arrow: MIRAGON.kontur,
  accent: MIRAGON.blau,
  accentSoft: "rgba(51,93,229,0.12)",
  /**
   * Text inside a sticky on its kind's fill: #1D1D1D reaches 4.5:1 on all eight fills, the hotspot
   * included. A fill the user picks gets black or white text, whichever reads better
   * (`textColorOn`).
   */
  stickyText: MIRAGON.schwarz,
} as const;

/**
 * Default stroke for freeform drawings and the draw-tool preview. A literal, never a `var()`: SVG
 * attributes travel into exports and other tools, which do not resolve CSS variables (guide §10.2).
 */
export const DRAWING_INK = MIRAGON.schwarz;

export const FONT = {
  /**
   * Set as the `font-family` attribute DIRECTLY on all SVG text elements (canvas labels and
   * consequently the SVG export too) – not inherited from a CSS container. In EVERY context it
   * reaches for the self-hosted Geist (Miragon corporate typeface) if the consumer provides it;
   * otherwise (e.g. an export SVG opened standalone) it falls back safely to a system sans. The
   * library does NOT ship the font – consumers must self-host Geist themselves
   * (e.g. via @fontsource-variable/geist), see README. 'Geist Variable' comes first because that
   * is the family name `@fontsource-variable/geist` registers; 'Geist' matches a locally
   * installed (static) Geist.
   */
  family:
    "'Geist Variable', 'Geist', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  label: 13,
} as const;
